import { getCachedValue, setCachedValue } from '../utils/clientCache';
import type { WordPressDraftListItem, WordPressDraftPost } from '../types/wordpress';

type BlogListResponse = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  items: WordPressDraftListItem[];
  categories: { id: number; name: string; slug: string }[];
  tags: { id: number; name: string; slug: string }[];
};

const CACHE_TTL_MS = 5 * 60 * 1000;

const buildUrl = (path: string, params: Record<string, string | number | undefined>) => {
  const base = (import.meta as any).env?.VITE_API_BASE_URL || '';
  const normalizedBase = base ? base.replace(/\/+$/, '') : window.location.origin;
  const url = new URL(`${normalizedBase}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};

const fetchJson = async <T>(url: string): Promise<T> => {
  const res = await fetch(url);
  const data = await res.json().catch(() => ({} as T));
  if (!res.ok) {
    throw new Error((data as any)?.error || `Erro HTTP ${res.status}`);
  }
  return data as T;
};

export const getBlogList = async (params: {
  page: number;
  perPage: number;
  search?: string;
  category?: string;
  tag?: string;
}): Promise<BlogListResponse> => {
  const url = buildUrl('/api/blog', {
    page: params.page,
    per_page: params.perPage,
    search: params.search,
    category: params.category,
    tag: params.tag,
  });
  const cacheKey = `blog-list:${url}`;
  const cached = getCachedValue<BlogListResponse>(cacheKey);
  if (cached) return cached;
  const data = await fetchJson<BlogListResponse>(url);
  setCachedValue(cacheKey, data, CACHE_TTL_MS);
  return data;
};

export const getBlogPost = async (slug: string): Promise<WordPressDraftPost> => {
  const url = buildUrl(`/api/blog/${encodeURIComponent(slug)}`, {});
  const cacheKey = `blog-post:${url}`;
  const cached = getCachedValue<WordPressDraftPost>(cacheKey);
  if (cached) return cached;
  const data = await fetchJson<WordPressDraftPost>(url);
  setCachedValue(cacheKey, data, CACHE_TTL_MS);
  return data;
};
